import {
  useId,
  type InputHTMLAttributes,
  type ReactNode,
  type SelectHTMLAttributes,
  type TextareaHTMLAttributes,
} from 'react';
import { charCount } from '../../core/text';
import { cn } from '../../lib/cn';

/**
 * Form fields.
 *
 * Every field here is built label-first: the label element is always rendered
 * and always wired to its control through a generated id, so there is no way to
 * add an unlabelled input to this app by accident. Hints and counters are joined
 * into `aria-describedby`, which is what makes a screen reader read out "220
 * characters maximum, 41 used" instead of leaving it as decoration.
 */

const CONTROL = cn(
  'w-full rounded-field border border-line bg-card px-3 py-2 text-sm text-strong',
  'placeholder:text-subtle',
  'transition-[border-color,box-shadow] duration-150',
  'hover:border-ink-300 dark:hover:border-ink-700',
  'focus:border-brand-500 focus:shadow-ring focus:outline-none',
  'disabled:cursor-not-allowed disabled:opacity-60'
);

interface CounterState {
  count: number;
  /** Hard cap. Past this LinkedIn truncates, so it reads as an error. */
  hard: number;
  /** Below this the field is too thin to be useful — a nudge, not an error. */
  min?: number;
}

function counterTone({ count, hard, min }: CounterState): { className: string; note: string } {
  if (count > hard) {
    return {
      className: 'text-critical-600 dark:text-critical-400 font-medium',
      note: `${count - hard} over the limit — LinkedIn will cut the end off`,
    };
  }
  if (count === 0) return { className: 'text-subtle', note: '' };
  if (min !== undefined && count < min) {
    return {
      className: 'text-important-700 dark:text-important-400',
      note: `${min - count} more characters recommended`,
    };
  }
  return { className: 'text-subtle', note: '' };
}

interface FieldShellProps {
  id: string;
  label: ReactNode;
  hint?: ReactNode;
  hintId?: string;
  counter?: CounterState;
  counterId?: string;
  /** Right-aligned slot next to the label — a section score pip, usually. */
  aside?: ReactNode;
  children: ReactNode;
  className?: string;
}

function FieldShell({
  id,
  label,
  hint,
  hintId,
  counter,
  counterId,
  aside,
  children,
  className,
}: FieldShellProps) {
  const tone = counter ? counterTone(counter) : null;

  return (
    <div className={cn('flex flex-col gap-1.5', className)}>
      <div className="flex items-baseline justify-between gap-3">
        <label htmlFor={id} className="text-sm font-medium text-strong">
          {label}
        </label>
        {aside}
      </div>

      {hint ? (
        <p id={hintId} className="text-xs leading-relaxed text-muted">
          {hint}
        </p>
      ) : null}

      {children}

      {counter && tone ? (
        <p id={counterId} className={cn('text-xs tabular-nums', tone.className)}>
          {counter.count.toLocaleString()} / {counter.hard.toLocaleString()}
          {tone.note ? <span className="ml-1.5 font-normal">· {tone.note}</span> : null}
        </p>
      ) : null}
    </div>
  );
}

/** Joins the ids that actually exist, so `aria-describedby` is never `"undefined"`. */
function describedBy(...ids: (string | false | undefined)[]): string | undefined {
  const present = ids.filter((id): id is string => Boolean(id));
  return present.length > 0 ? present.join(' ') : undefined;
}

export interface TextFieldProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'id'> {
  label: ReactNode;
  hint?: ReactNode;
  aside?: ReactNode;
  /** Enables the character counter. Values come from `core/data/limits.ts`. */
  limit?: { hard: number; min?: number };
  containerClassName?: string;
}

export function TextField({
  label,
  hint,
  aside,
  limit,
  className,
  containerClassName,
  value,
  ...props
}: TextFieldProps) {
  const base = useId();
  const hintId = hint ? `${base}-hint` : undefined;
  const counterId = limit ? `${base}-counter` : undefined;
  const text = typeof value === 'string' ? value : '';

  return (
    <FieldShell
      id={base}
      label={label}
      hint={hint}
      hintId={hintId}
      aside={aside}
      className={containerClassName}
      counter={limit ? { count: charCount(text), hard: limit.hard, min: limit.min } : undefined}
      counterId={counterId}
    >
      <input
        id={base}
        value={value}
        aria-describedby={describedBy(hintId, counterId)}
        className={cn(CONTROL, className)}
        {...props}
      />
    </FieldShell>
  );
}

export interface TextAreaFieldProps extends Omit<
  TextareaHTMLAttributes<HTMLTextAreaElement>,
  'id'
> {
  label: ReactNode;
  hint?: ReactNode;
  aside?: ReactNode;
  limit?: { hard: number; min?: number };
  containerClassName?: string;
}

export function TextAreaField({
  label,
  hint,
  aside,
  limit,
  className,
  containerClassName,
  value,
  rows = 5,
  ...props
}: TextAreaFieldProps) {
  const base = useId();
  const hintId = hint ? `${base}-hint` : undefined;
  const counterId = limit ? `${base}-counter` : undefined;
  const text = typeof value === 'string' ? value : '';

  return (
    <FieldShell
      id={base}
      label={label}
      hint={hint}
      hintId={hintId}
      aside={aside}
      className={containerClassName}
      counter={limit ? { count: charCount(text), hard: limit.hard, min: limit.min } : undefined}
      counterId={counterId}
    >
      <textarea
        id={base}
        rows={rows}
        value={value}
        aria-describedby={describedBy(hintId, counterId)}
        className={cn(CONTROL, 'min-h-24 leading-relaxed', className)}
        {...props}
      />
    </FieldShell>
  );
}

export interface SelectFieldProps extends Omit<SelectHTMLAttributes<HTMLSelectElement>, 'id'> {
  label: ReactNode;
  hint?: ReactNode;
  containerClassName?: string;
}

export function SelectField({
  label,
  hint,
  className,
  containerClassName,
  children,
  ...props
}: SelectFieldProps) {
  const base = useId();
  const hintId = hint ? `${base}-hint` : undefined;

  return (
    <FieldShell id={base} label={label} hint={hint} hintId={hintId} className={containerClassName}>
      <select
        id={base}
        aria-describedby={hintId}
        className={cn(CONTROL, 'appearance-none bg-no-repeat pr-9', className)}
        style={{
          // A chevron drawn in CSS rather than an icon component, so the native
          // dropdown keeps its platform behaviour on mobile.
          backgroundImage:
            "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 20 20' fill='%2394a3b8'%3E%3Cpath d='M5.2 7.3a1 1 0 0 1 1.4 0L10 10.6l3.4-3.3a1 1 0 1 1 1.4 1.4l-4.1 4a1 1 0 0 1-1.4 0l-4.1-4a1 1 0 0 1 0-1.4Z'/%3E%3C/svg%3E\")",
          backgroundPosition: 'right 0.6rem center',
          backgroundSize: '1.25rem',
        }}
        {...props}
      >
        {children}
      </select>
    </FieldShell>
  );
}

export interface CheckFieldProps extends Omit<
  InputHTMLAttributes<HTMLInputElement>,
  'id' | 'type'
> {
  label: ReactNode;
  hint?: ReactNode;
}

/**
 * A checkbox row for the profile checklist.
 *
 * A real `<input type="checkbox">` rather than a styled `div`: it arrives with
 * keyboard support, the correct role, and form semantics already working. Only
 * the visual box is restyled.
 */
export function CheckField({ label, hint, className, ...props }: CheckFieldProps) {
  const base = useId();
  const hintId = hint ? `${base}-hint` : undefined;

  return (
    <div className={cn('flex gap-3', className)}>
      <input
        id={base}
        type="checkbox"
        aria-describedby={hintId}
        className={cn(
          'mt-0.5 size-4 shrink-0 cursor-pointer appearance-none rounded border border-ink-300 bg-card',
          'checked:border-brand-600 checked:bg-brand-600',
          'dark:border-ink-600 dark:checked:border-brand-500 dark:checked:bg-brand-500',
          'bg-center bg-no-repeat checked:bg-[length:0.75rem]',
          'transition-colors duration-150',
          "checked:bg-[url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 16 16' fill='white'%3E%3Cpath d='M13.3 4.3a1 1 0 0 1 0 1.4l-6 6a1 1 0 0 1-1.4 0l-3-3a1 1 0 0 1 1.4-1.4L6.6 9.6l5.3-5.3a1 1 0 0 1 1.4 0Z'/%3E%3C/svg%3E\")]"
        )}
        {...props}
      />
      <div className="min-w-0">
        <label htmlFor={base} className="cursor-pointer text-sm text-strong">
          {label}
        </label>
        {hint ? (
          <p id={hintId} className="mt-0.5 text-xs text-muted">
            {hint}
          </p>
        ) : null}
      </div>
    </div>
  );
}
